import { computed, ref, Ref } from "vue";
import { storeToRefs } from "pinia";

import { useAuthStore } from "@/stores/Auth";
import { useTradeAlertsStore } from "@/stores/TradeAlerts";

import { useDateRangeFilter } from "@/composables/filters/date-range-filter";
import { usePagingFilter } from "@/composables/filters/paging-filter";
import { useProductFilter } from "@/composables/filters/product-filter";
import { useTextFilter } from "@/composables/filters/text-filter";
import { SearchableTicker, TradeAlert } from "@/types/entities";

import { DateTime } from "luxon";

/*********************************************************************/
//                       NON-REACTIVE CONSTANTS                      //
/*********************************************************************/
import { consoleLoggerFactory, getDefaultLoggerOptions, ILogger, LogLevels } from "@/types/console-logger";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

/*********************************************************************/
//                 GLOBALLY SHARED REACTIVE VARIABLES                //
/*********************************************************************/

export function useTradeAlertFilters() {
    const { isAuthenticated } = storeToRefs( useAuthStore() );

    const tradeAlertsStore = useTradeAlertsStore();
    const { allTradeAlerts } = storeToRefs( tradeAlertsStore );

    // -------------------------------- FILTER STATE --------------------------------- //
    const hasAnyTradeAlertResults = computed< boolean >( (): boolean => filteredTradeAlerts.value.length > 0 );
    const tradeAlertsSearchResultsLabel = computed< string >( (): string => `${ filteredTradeAlerts.value.length } trade alert${ filteredTradeAlerts.value.length > 1 ? 's' : '' } found` );

    // ------------------------------------ FILTERS ------------------------------------
    // The time interval that captures the date range for which to return trade alerts
    const { validDateInterval, isValidDateWithinDateRange } = useDateRangeFilter();

    // The paging filter to manage what is displayed on a per-page basis
    const { filteredContentPage } = usePagingFilter();

    // The products that have been selected to limit the results (inclusive)
    const { filterOutItemsThatDoNotContainSelectedProducts } = useProductFilter();

    // The search text entered by the user
    const { filterOutItemsThatDoNotContainSearchText } = useTextFilter();

    const loading = ref< boolean >( false );

    const filteredTradeAlertsPage = computed< TradeAlert[] >(() => {
        return filteredContentPage< TradeAlert >( filteredTradeAlerts.value );
    });

    const filteredTradeAlerts = computed< TradeAlert[] >( () => {
        loading.value = true;
        let tradeAlertsAfterFiltering: TradeAlert[] = [];

        try {
            logger.debug( '[ trade-alert-filter ].filteredTradeAlerts -> starting computation.' );

            logger.debug( '[ trade-alert-filter ].filteredTradeAlerts -> Date Interval filtering (START): ', validDateInterval.value.start?.toLocaleString( DateTime.DATE_FULL ) );
            logger.debug( '[ trade-alert-filter ].filteredTradeAlerts -> Date Interval filtering (END): ', validDateInterval.value.end?.toLocaleString( DateTime.DATE_FULL ) );

            const composedTradeAlert: Ref< TradeAlert[]> = allTradeAlerts;
            if ( isAuthenticated.value ) {
                logger.debug( `[ trade-alert-filter ].filteredTradeAlerts -> authenticated, filtering tradeAlerts based on short-code access.` );
                tradeAlertsAfterFiltering =
                    composedTradeAlert.value
                                      // Filter by any products
                                      .filter( ( tradeAlert: TradeAlert ) => filterOutItemsThatDoNotContainSelectedProducts( tradeAlert.products ) )
                                      // Filter by date range interval
                                      .filter( ( tradeAlert: TradeAlert ) => isValidDateWithinDateRange( tradeAlert.publishedDate ) )
                                      // Filter by text search criteria
                                      .filter( ( tradeAlert: TradeAlert ) =>
                                          filterOutItemsThatDoNotContainSearchText( [
                                              ...tradeAlert.tickers.map( ( ticker: SearchableTicker ) => ticker.name ),
                                              tradeAlert.text,
                                              tradeAlert.body2,
                                              tradeAlert.title
                                          ] ) );
            }

            logger.debug( `[ trade-alert-filter ].filteredTradeAlerts -> tradeAlertsAfterFiltering : `, tradeAlertsAfterFiltering );
        } finally {
            loading.value = false;
        }

        return tradeAlertsAfterFiltering;
    } );

    return {
        // T/F based on if filteredTradeAlerts has any items
        hasAnyTradeAlertResults,
        // '#### results found'
        tradeAlertsSearchResultsLabel,

        // The current single page sub-collection
        filteredTradeAlertsPage,
        // All trade alerts that match the current filter
        filteredTradeAlerts,

        // State variable to track if the filter is currently processing anything
        loading
    }
}


export type TradeAlertFilters = ReturnType< typeof useTradeAlertFilters >;
